import { useState } from 'react';
import { Calculator, Check, Plus, RotateCcw, X } from 'lucide-react';
import { uid } from '../state';
import { FONT_BODY, FONT_MONO } from '../theme';
import { Button, NumberInput } from '../ui';

export function FoodEstimateSheet({ t, token, groupLabel, initialDescription, onLog, onClose }) {
  const [description, setDescription] = useState(initialDescription || '');
  const [phase, setPhase] = useState('input');
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  const estimate = async () => {
    const clean = description.trim();
    if (clean.length < 2) return;
    setPhase('estimating');
    try {
      const res = await fetch('/api/food/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ description: clean }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !Array.isArray(data.items) || !data.items.length) {
        setError(data.message || 'No food estimate was returned. Add a little more detail and try again.');
        setPhase('error');
        return;
      }
      setItems(data.items.map(item => ({
        ...item,
        grams: Math.max(1, Math.round(item.estimated_grams || 100)),
        included: true,
      })));
      setPhase('review');
    } catch {
      setError('Food estimation failed. Check your connection and try again.');
      setPhase('error');
    }
  };

  const itemMacros = (item) => {
    const factor = (item.grams || 0) / 100;
    const per100 = item.per100 || {};
    return {
      kcal: (per100.kcal || 0) * factor,
      protein: (per100.protein || 0) * factor,
      carbs: (per100.carbs || 0) * factor,
      fats: (per100.fats || 0) * factor,
    };
  };
  const included = items.filter(item => item.included);
  const totalKcal = included.reduce((sum, item) => sum + itemMacros(item).kcal, 0);
  const logAll = () => {
    included.forEach(item => {
      const per100 = item.per100 || {};
      const unit = item.unit === 'ml' ? 'ml' : 'g';
      onLog({
        id: uid('food'), name: item.name, brand: '', unit,
        per: {
          kcal: (per100.kcal || 0) / 100,
          protein: (per100.protein || 0) / 100,
          carbs: (per100.carbs || 0) / 100,
          fats: (per100.fats || 0) / 100,
        },
        portion: item.grams, serving: 'AI estimate', source: 'estimate', code: '',
      });
    });
    onClose();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={event => event.stopPropagation()} style={{ background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + t.divider, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}><Calculator size={17} /> Estimate for {groupLabel}</div>
          <button onClick={onClose} title='Close' style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: 4, display: 'flex' }}><X size={18} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {phase === 'input' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <textarea autoFocus value={description} onChange={event => setDescription(event.target.value)} rows={4}
                placeholder='e.g. 1 lahmacun with cheese, garlic sauce and a can of cola'
                style={{ width: '100%', resize: 'vertical', background: t.inputBg, color: t.text, border: '1px solid ' + t.inputBorder, borderRadius: 8, padding: '10px 12px', fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.45, outline: 'none' }} />
              <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5 }}>Mention quantity, cheese, sauce, oil and drinks for a more useful estimate.</div>
              <Button t={t} variant='primary' disabled={description.trim().length < 2} onClick={estimate} style={{ justifyContent: 'center' }}><Calculator size={15} /> Estimate macros</Button>
            </div>
          )}

          {phase === 'estimating' && <div style={{ padding: '32px 8px', textAlign: 'center', color: t.textMuted, fontSize: 14 }}>Estimating portions and macros...</div>}

          {phase === 'error' && (
            <div style={{ padding: '24px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.danger, marginBottom: 8 }}>Could not estimate this meal</div>
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>{error}</div>
              <Button t={t} variant='primary' onClick={() => setPhase('input')}><RotateCcw size={14} /> Edit description</Button>
            </div>
          )}

          {phase === 'review' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map((item, index) => {
                const macros = itemMacros(item);
                return (
                  <div key={item.name + '-' + index} style={{ padding: '10px 0', borderBottom: '1px solid ' + t.divider, display: 'flex', alignItems: 'center', gap: 10, opacity: item.included ? 1 : 0.45 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                      <div style={{ marginTop: 2, fontFamily: FONT_MONO, fontSize: 11, color: t.textFaint }}>{Math.round(macros.kcal)} kcal | {Math.round(macros.protein)}P {Math.round(macros.carbs)}C {Math.round(macros.fats)}F</div>
                    </div>
                    <div style={{ width: 84 }}><NumberInput t={t} value={item.grams} step={10} min={0} suffix={item.unit === 'ml' ? 'ml' : 'g'} onChange={value => setItems(prev => prev.map((entry, itemIndex) => itemIndex === index ? { ...entry, grams: Math.max(0, Number(value) || 0) } : entry))} /></div>
                    <button onClick={() => setItems(prev => prev.map((entry, itemIndex) => itemIndex === index ? { ...entry, included: !entry.included } : entry))} title={item.included ? 'Exclude' : 'Include'} style={{ flexShrink: 0, height: 32, width: 32, borderRadius: 8, cursor: 'pointer', border: '1px solid ' + (item.included ? t.accent : t.cardBorder), background: item.included ? t.accent : 'transparent', color: item.included ? '#fff' : t.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={15} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {phase === 'review' && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid ' + t.divider }}>
            <Button t={t} variant='primary' disabled={!included.length} onClick={logAll} style={{ width: '100%', justifyContent: 'center' }}><Plus size={14} /> Log {included.length} item{included.length === 1 ? '' : 's'} | {Math.round(totalKcal)} kcal</Button>
          </div>
        )}
      </div>
    </div>
  );
}
